import React, { useState, useEffect } from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
//icon
import { AiOutlineEye } from 'react-icons/ai';
import { useBlogCategoriesStore } from 'stores';
//helpers
import { verificateNullValue } from 'helpers';
// components
import Button from 'components/backoffice/buttonCustom';
import Input from 'components/backoffice/customInput';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import WatchModal from 'components/backoffice/modals/WatchModal';
import CkEditorComponent from 'components/backoffice/inputEditor/InputEditorComponent';
import { SelectInputCustom } from 'components/input/index';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const PostsForm = ({
  isEditing,
  onSubmitForm,
  fileInputRef,
  onDelete,
  openModalDelete,
  setOpenModalDelete,
  defaultValues,
  beforeTitle,
  loading,
}) => {
  const { t } = useTranslation(['backoffice']);
  const [openModalImage, setOpenModalImage] = useState(false);
  const { categories: blogCategories } = useBlogCategoriesStore();
  const [previewImage, setPreviewImage] = useState(null);
  const {
    formState: { errors },
    setValue,
    reset,
    watch,
    control,
    handleSubmit,
    getValues,
  } = useForm();
  const imageValue = watch('featured_image');

  const getImageForModal = () => {
    if (previewImage) return previewImage;
    if (typeof imageValue === 'string') return imageValue;
    if (imageValue instanceof Blob || imageValue instanceof File) {
      return URL.createObjectURL(imageValue);
    }
    return '';
  };

  const watchItem = () => {
    setOpenModalImage(true);
  };

  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  let item;
  const image = getValues('featured_image');

  if (isEditing) {
    item =
      typeof image === 'string'
        ? image
        : image instanceof Blob || image instanceof File
          ? URL.createObjectURL(image)
          : '';
  } else {
    item = image !== undefined ? URL.createObjectURL(image) : '';
  }

  const status = [
    { id: 'draft', value: t('draft') },
    { id: 'published', value: t('published') },
    { id: 'hidden', value: t('hidden') },
  ];

  const categoriesSelect = blogCategories?.map((item) => ({
    id: item.id,
    value: item.name,
  }));


  const hasValidDefaultValues = (values) => {
    return Object.values(values || {}).some(
      (v) => v !== null && v !== undefined && v !== '' && !(typeof v === 'object' && Object.keys(v || {}).length === 0),
    );
  };

  useEffect(() => {
    if (!hasValidDefaultValues(defaultValues)) return;
    if (isEditing) {
      setValue('featured_image', null);
      if (defaultValues?.featured_image) {
        fetch(defaultValues.featured_image)
          .then((response) => response.blob())
          .then((blob) => {
            const name = defaultValues.featured_image.split('?')[0].split('/').pop();
            const file = new File([blob], name, { type: `image/${name.split('.')[1]}` });
            setValue('featured_image', file);
          })
          .catch((error) => {
            console.error('Error al obtener la imagen:', error);
          });
      }
    } else {
      setValue('featured_image', defaultValues.featured_image);
    }
    setValue('title', verificateNullValue(defaultValues.title));
    setValue('featured_paragraph', defaultValues.featured_paragraph);
    setValue('body', verificateNullValue(defaultValues.body));
    setValue('status', defaultValues.status);
    setValue('category', defaultValues.category?.id ?? null);
  }, [defaultValues]);

  return (
    <div className="mt-5">
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
            <Controller
              name="title"
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  type="text"
                  label={t('titulo')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('titulo')}
                />
              )}
            />
            {errors?.title && <small className="text-danger ">{errors.title.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name="featured_paragraph"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  type="text"
                  label={t('featuredParagraph')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('featuredParagraph')}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="category"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <SelectInputCustom
                  choices={categoriesSelect}
                  label={`${t('category')}`}
                  placeholder="Seleccionar"
                  onCheckedItem={(e) => onChange(e)}
                  value={value}
                />
              )}
            />
            {errors?.category && <small className="text-danger ">{errors.category.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="d-flex mt-4 mt-lg-0 mb-md-2 mb-lg-0 gap-3">
            <div className="w-100">
              <Controller
                name="featured_image"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    type="file"
                    dropFileMsg={t('dropFile')}
                    label={t('imagen')}
                    placeholder={t('selectImg')}
                    accept="image/*"
                    value={value}
                    onChange={(e) => {
                      const file = e instanceof File ? e : e?.target?.files?.[0];
                      if (file) {
                        setPreviewImage(URL.createObjectURL(file));
                      } else {
                        setPreviewImage(null);
                      }
                      onChange(e);
                    }}
                    ref={fileInputRef}
                  />
                )}
              />
              {errors?.featured_image && (
                <small className="text-danger position-text-required">{errors.featured_image.message}</small>
              )}
            </div>
            <div className="mt-4-3">
              <Button color={'terciary'} type="button" onClick={watchItem}>
                <AiOutlineEye color="white" />
              </Button>
            </div>
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name="status"
              control={control}
              render={({ field: { onChange, value } }) => (
                <SelectInputCustom
                  choices={status}
                  label={`${t('state')}`}
                  placeholder="Seleccionar"
                  value={value}
                  onCheckedItem={(e) => onChange(e)}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} className="mb-md-2 mb-lg-0">
            <Form.Label className="text-light">{t('text')}</Form.Label>
            <Controller
              name="body"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => {
                return <CkEditorComponent value={value} onChange={(content) => onChange(content)} />;
              }}
            />
            {errors?.body && <small className="text-danger ">{errors.body.message}</small>}
          </Col>
        </Row>
      </Form>
      <div className="w-90 my-4 d-flex justify-content-end gap-3">
        <Button color="primary" onClick={handleSubmit(onSubmitForm)} loading={loading}>
          {t('save')}
          <SpinnerComponent loading={loading} />
        </Button>
        {isEditing && (
          <Button color="secondary" onClick={() => setOpenModalDelete(true)} loading={loading}>
            {t('eliminar')}
          </Button>
        )}
      </div>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={onDelete}
        deleteTitle={t('deletepost')}
        beforeTitle={t('edit', { title: defaultValues?.title })}
      />
      <WatchModal
        item={getImageForModal()}
        title={t('imagen')}
        show={openModalImage}
        setShow={setOpenModalImage}
        beforeTitle={beforeTitle ? beforeTitle : t('edit', { title: defaultValues?.title })}
      />
    </div>
  );
};

PostsForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  openModalDelete: PropTypes.bool,
  setOpenModalDelete: PropTypes.func,
  onDelete: PropTypes.func,
  id: PropTypes.any,
  beforeTitle: PropTypes.string,
  loading: PropTypes.bool,
};

export default PostsForm;